/* ============================================================================
   HASK — Issue 01 · interaction engine (A1 foundation)
   - Tear-card: one-way torn state on [data-hask-tear] wrappers (spec §4).
   - POM: click a size chip [data-hask-size] -> highlight [data-hask-pom-row].
   - Add-to-bag toast (spec §2.6) as progressive enhancement (forms work w/o JS).
   - Grain overlay: inject .hask-grain if it is missing.
   All behaviour degrades gracefully; nothing here is required for the page to
   function. Vanilla JS, no dependencies. Loaded deferred from theme.liquid.
   ============================================================================ */
(function () {
  'use strict';

  var prefersReduced = false;
  try {
    prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    prefersReduced = false;
  }

  var isRTL = document.documentElement.getAttribute('dir') === 'rtl';

  /* -------------------------------------------------------------------------
     1. Grain overlay: ensure the fixed grain div exists (desktop CSS shows it).
     ------------------------------------------------------------------------- */
  function ensureGrain() {
    if (!document.querySelector('.hask-grain')) {
      var grain = document.createElement('div');
      grain.className = 'hask-grain';
      grain.setAttribute('aria-hidden', 'true');
      document.body.insertBefore(grain, document.body.firstChild);
    }
  }

  /* -------------------------------------------------------------------------
     2. Tear-card engine (spec §4). Verbatim transforms/transitions.
        Wrapper:  [data-hask-tear]
        Pieces:   .hask-tearpiece[data-piece="A"|"B"] (inside wrapper)
        Reveal:   [data-hask-reveal] (quick-add panel; pointer-events gated)
        One-way: once torn, stays torn (per-card).
     ------------------------------------------------------------------------- */
  var TEAR_TRANS =
    'transform 1.25s cubic-bezier(.3,.65,.25,1), opacity .92s ease .3s';
  var TFORM_A = 'translate(-16%, -84%) rotate(-11deg)'; /* piece A flies up-left */
  var TFORM_B = 'translate(14%, 92%) rotate(8deg)'; /* piece B falls down-right */

  function tearCard(wrapper) {
    if (!wrapper) return;
    if (
      wrapper.getAttribute('data-hask-torn') === 'true' ||
      wrapper.getAttribute('data-torn') === 'true' ||
      wrapper.classList.contains('is-torn')
    ) {
      return;
    }

    var pieces = wrapper.querySelectorAll('.hask-tearpiece');

    /* Class-based cards (e.g. the tear-grid section) bake the spec §4 transforms
       for BOTH pieces into CSS, keyed off .is-torn / [data-torn="true"]. Their
       pieces carry no [data-piece] attribute. For those, drive the tear via the
       class mechanism rather than stomping inline styles — otherwise every piece
       (having no data-piece) would fall back to TFORM_A and piece B would fly the
       wrong way. Inline styles below are reserved for cards that opt into the A1
       contract by tagging pieces with data-piece="A"/"B". */
    var hasDataPiece = false;
    for (var d = 0; d < pieces.length; d++) {
      if (pieces[d].getAttribute('data-piece')) {
        hasDataPiece = true;
        break;
      }
    }

    if (pieces.length && !hasDataPiece) {
      wrapper.classList.add('hask-tear-js');
      if (prefersReduced) wrapper.classList.add('hask-tear-anim-off');
      wrapper.classList.add('is-torn');
      wrapper.setAttribute('data-torn', 'true');
      wrapper.setAttribute('data-hask-torn', 'true');
      wrapper.style.cursor = 'default';
      return;
    }

    wrapper.setAttribute('data-hask-torn', 'true');

    var animate = !prefersReduced;
    for (var i = 0; i < pieces.length; i++) {
      var p = pieces[i];
      var which = p.getAttribute('data-piece');
      p.style.transition = animate ? TEAR_TRANS : 'none';
      p.style.opacity = '0';
      p.style.transform = which === 'B' ? TFORM_B : TFORM_A;
    }

    /* enable the revealed quick-add panel + drop the "tear" cursor */
    wrapper.style.cursor = 'default';
    var reveal = wrapper.querySelector('[data-hask-reveal]');
    if (reveal) reveal.style.pointerEvents = 'auto';
  }

  function onTearActivate(target) {
    var wrapper = target.closest ? target.closest('[data-hask-tear]') : null;
    if (!wrapper) return false;
    /* Do not tear when the click is on an interactive control inside the
       already-revealed panel (buttons, links, size chips, form fields). */
    if (wrapper.getAttribute('data-hask-torn') === 'true') return false;
    tearCard(wrapper);
    return true;
  }

  /* -------------------------------------------------------------------------
     3. POM selected-row highlight.
        A size chip carries [data-hask-size="M"]; clicking it highlights the
        matching [data-hask-pom-row="M"] within the same section/product scope.
     ------------------------------------------------------------------------- */
  function highlightPomRow(sizeEl) {
    var size = sizeEl.getAttribute('data-hask-size');
    if (!size) return;
    /* scope: nearest section, else document */
    var scope =
      sizeEl.closest('.shopify-section') ||
      sizeEl.closest('[data-hask-product]') ||
      document;
    var rows = scope.querySelectorAll('[data-hask-pom-row]');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].getAttribute('data-hask-pom-row') === size) {
        rows[i].classList.add('hask-pom-row--on');
      } else {
        rows[i].classList.remove('hask-pom-row--on');
      }
    }
  }

  /* -------------------------------------------------------------------------
     4. Add-to-bag toast (spec §2.6). Progressive enhancement:
        intercept submit of forms carrying [data-hask-atc], POST to Shopify's
        /cart/add.js, show the toast, and update any cart-count badges. If fetch
        fails or is unavailable, fall through to the native form POST (which
        redirects to the Bag page) so the form still works with JS disabled.
     ------------------------------------------------------------------------- */
  var toastTimer = null;

  function removeToast() {
    var existing = document.querySelector('.hask-toast');
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
  }

  function showToast(message) {
    removeToast();
    var isMobile = window.matchMedia('(max-width: 740px)').matches;
    var toast = document.createElement('div');
    toast.className = 'hask-toast hask-anim';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');

    var msg = document.createElement('span');
    msg.className = 'hask-toast__msg';
    msg.textContent = message;
    toast.appendChild(msg);

    if (!isMobile) {
      var view = document.createElement('a');
      view.className = 'hask-toast__view';
      view.href = '/cart';
      view.textContent = isRTL ? 'عرض الحقيبة' : 'VIEW BAG';
      toast.appendChild(view);
    }

    document.body.appendChild(toast);
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(removeToast, isMobile ? 2600 : 3400);
  }

  function updateCartCount(count) {
    var badges = document.querySelectorAll('[data-hask-cart-count]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = isRTL ? toArabicDigits(String(count)) : String(count);
      badges[i].style.display = count > 0 ? '' : 'none';
    }
  }

  function toArabicDigits(s) {
    var map = {
      '0': '٠', '1': '١', '2': '٢', '3': '٣', '4': '٤',
      '5': '٥', '6': '٦', '7': '٧', '8': '٨', '9': '٩', ',': '٬'
    };
    return String(s).replace(/[0-9,]/g, function (c) {
      return map[c] || c;
    });
  }

  function toastMessage(form) {
    /* Prefer explicit data attributes on the form; fall back gracefully. */
    var name = form.getAttribute('data-hask-name') || '';
    var size = form.getAttribute('data-hask-size-label') || '';
    var isMobile = window.matchMedia('(max-width: 740px)').matches;
    var pieces = [];
    if (name) pieces.push(name);
    if (size) pieces.push(size);
    var head = pieces.join(' · ');
    if (isRTL) {
      return head + (isMobile ? ' — أُضيفت' : ' — أُضيفت إلى حقيبتك');
    }
    return head + (isMobile ? ' — added' : ' — added to your bag');
  }

  function handleAtcSubmit(form, evt) {
    if (!window.fetch || !window.FormData) return; /* native POST proceeds */
    evt.preventDefault();

    var data = new FormData(form);
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: data
    })
      .then(function (res) {
        if (!res.ok) throw new Error('add failed');
        return res.json();
      })
      .then(function () {
        return fetch('/cart.js', { headers: { Accept: 'application/json' } });
      })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        showToast(toastMessage(form));
        document.dispatchEvent(
          new CustomEvent('hask:cart:updated', { detail: cart })
        );
      })
      .catch(function () {
        /* On any failure, fall back to a normal navigation so the user still
           reaches the Bag with the item added by re-submitting natively. */
        form.submit();
      });
  }

  /* -------------------------------------------------------------------------
     5. Wire up delegated listeners.
     ------------------------------------------------------------------------- */
  function init() {
    ensureGrain();

    /* Claim the tear engine so section-local fallbacks (which check
       window.HaskTear) stand down — only ONE engine drives each card. */
    window.HaskTear = { version: 1 };

    /* Prime every tear card: class-based cards keep their paper pieces hidden
       until .hask-tear-js is present (CSS gate). Replicate the section's own
       setup so the pieces render before the tear, and honour reduced motion. */
    var cards = document.querySelectorAll('[data-hask-tear]');
    for (var c = 0; c < cards.length; c++) {
      cards[c].classList.add('hask-tear-js');
      if (prefersReduced) cards[c].classList.add('hask-tear-anim-off');
    }

    /* Tear + POM on click (delegated). */
    document.addEventListener(
      'click',
      function (e) {
        var t = e.target;
        if (!t || !t.closest) return;

        /* POM highlight (also allowed inside tear reveal panels). */
        var sizeEl = t.closest('[data-hask-size]');
        if (sizeEl) highlightPomRow(sizeEl);

        /* Tear: only if the click is NOT on an interactive reveal control. */
        var interactive = t.closest(
          'a, button, input, select, textarea, label, [data-hask-reveal]'
        );
        var tearWrap = t.closest('[data-hask-tear]');
        if (tearWrap && tearWrap.getAttribute('data-hask-torn') !== 'true') {
          if (!interactive || !interactive.closest('[data-hask-reveal]')) {
            onTearActivate(t);
          }
        }
      },
      false
    );

    /* Keyboard: Enter/Space tears a focused card wrapper. */
    document.addEventListener(
      'keydown',
      function (e) {
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        var t = e.target;
        if (!t || !t.closest) return;
        var tearWrap = t.closest('[data-hask-tear]');
        if (
          tearWrap &&
          tearWrap === t &&
          tearWrap.getAttribute('data-hask-torn') !== 'true'
        ) {
          e.preventDefault();
          tearCard(tearWrap);
        }
      },
      false
    );

    /* Add-to-bag toast: intercept flagged forms. */
    document.addEventListener(
      'submit',
      function (e) {
        var form = e.target;
        if (form && form.matches && form.matches('form[data-hask-atc]')) {
          handleAtcSubmit(form, e);
        }
      },
      false
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
